/**
 * أداة تشخيص أخطاء render:
 * تبني نصّاً واحداً يحوي المسار الحاليّ + سلسلة Breadcrumbs + رسالة الخطأ + stack
 * + معلومات المتصفّح، جاهزاً للصق في محادثة الدعم.
 */
import { BREADCRUMBS } from "@/lib/breadcrumbsMap";

export interface ErrorDiagnostics {
  pathname: string;
  breadcrumbs: Array<{ path: string; ar: string; en: string }>;
  breadcrumbMissing: boolean;
  errorName: string;
  errorMessage: string;
  errorStack: string;
  userAgent: string;
  viewport: string;
  timestamp: string;
}

function resolveChain(pathname: string): Array<{ path: string; ar: string; en: string }> {
  const clean = pathname.replace(/\/+$/, "") || "/";
  const out: Array<{ path: string; ar: string; en: string }> = [];
  const seen = new Set<string>();
  let cursor: string | null | undefined = clean;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const entry: { ar: string; en: string; parent?: string | null } | undefined = BREADCRUMBS[cursor];
    if (!entry) {
      // مسار غير موجود في الخريطة — أضِفه كما هو
      out.unshift({ path: cursor, ar: cursor, en: cursor });
      break;
    }
    out.unshift({ path: cursor, ar: entry.ar, en: entry.en });
    cursor = entry.parent ?? null;
  }
  return out;
}

export function buildErrorDiagnostics(error: unknown, pathname?: string): ErrorDiagnostics {
  const p =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const chain = resolveChain(p);
  const breadcrumbMissing = !BREADCRUMBS[p.replace(/\/+$/, "") || "/"];
  const err = error as { name?: string; message?: string; stack?: string } | undefined;
  return {
    pathname: p,
    breadcrumbs: chain,
    breadcrumbMissing,
    errorName: err?.name ?? "Error",
    errorMessage: err?.message ?? String(error ?? "unknown"),
    errorStack: err?.stack ?? "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    viewport:
      typeof window !== "undefined"
        ? `${window.innerWidth}x${window.innerHeight} (dpr=${window.devicePixelRatio || 1})`
        : "",
    timestamp: new Date().toISOString(),
  };
}

export function formatErrorDiagnostics(d: ErrorDiagnostics): string {
  const chain = d.breadcrumbs.map((c) => `${c.ar} (${c.path})`).join(" › ");
  return [
    `━━━ Lecmo Render Error — بيانات تشخيص ━━━`,
    `الوقت: ${d.timestamp}`,
    `المسار: ${d.pathname}${d.breadcrumbMissing ? "  ⚠️ لا مدخل Breadcrumb" : ""}`,
    `Breadcrumbs: ${chain || "(لا سلسلة)"}`,
    `الشاشة: ${d.viewport}`,
    `المتصفّح: ${d.userAgent}`,
    `━━━ الخطأ ━━━`,
    `${d.errorName}: ${d.errorMessage}`,
    d.errorStack ? `\nStack:\n${d.errorStack}` : "",
  ].join("\n");
}

/**
 * ينسخ التشخيص إلى الحافظة (يحاول Clipboard API ثم fallback عبر textarea).
 * يُعيد true عند النجاح.
 */
export async function copyErrorDiagnostics(d: ErrorDiagnostics): Promise<boolean> {
  const text = formatErrorDiagnostics(d);
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * يخزّن آخر تشخيص في sessionStorage ليقرأه المطوّر لاحقاً (window.__lecmoLastError).
 */
export function stashErrorDiagnostics(d: ErrorDiagnostics): void {
  try {
    if (typeof window === "undefined") return;
    const text = formatErrorDiagnostics(d);
    window.sessionStorage.setItem("lecmo:last-render-error", text);
    (window as unknown as { __lecmoLastError?: ErrorDiagnostics }).__lecmoLastError = d;
  } catch {
    /* ignore */
  }
}
